import { CITY } from '@city'
import { Link } from 'react-router-dom'
import { LegalLayout } from '../components/LegalLayout'
import { WebAnalyticsProviders } from './privacyPolicy/WebAnalyticsProviders'

/**
 * Privacy policy for london-tube.com (web) and the Tube Map & London
 * Underground mobile app (iOS + Android). Ported from the legacy
 * mmthstudio.com/tube/privacy-policy and corrected for the actual
 * third-party stack used by the Metro product (GA4 + Amplitude on web
 * where the city runs web analytics — `CITY.webAnalytics`, the InMobi
 * Choice consent window in every city, Apple MapKit JS on station pages; Amplitude + AppsFlyer + Adapty on
 * mobile, plus Google Maps SDK on Android). Firebase Cloud Messaging is linked into the Android
 * app but has no config in release builds, so it never runs for users and is
 * not listed.
 *
 * Linked from:
 *   - Footer of every page on london-tube.com
 *   - Google Play Console store listing
 *   - App Store Connect store listing
 *   - Cookie banner consent flow
 *
 * Tanya rewrites for tone in a separate task; legal substance must
 * stay accurate to what the apps and site actually do.
 */
export function PrivacyPolicyPage() {
  return (
    <LegalLayout current="privacy">
      <h1>Privacy Policy</h1>

      <p>
        This privacy policy applies to <strong>{CITY.domain}</strong> (the "Service") and the{' '}
        <strong>{CITY.serviceName}</strong> mobile app (the "Application") for iOS and Android, both
        operated by <strong>Individual Entrepreneur Pavel Kuzin</strong> ("Service Provider"). The
        Application is provided as a Freemium service "AS IS". This page explains what personal data
        we process and how to exercise your rights under the UK GDPR and the Data Protection Act
        2018.
      </p>

      <h2>What information does the Service obtain and how is it used?</h2>

      <h3>User Provided Information</h3>
      <p>
        The Application can be used without registration. If you choose to register or contact
        support, we receive the information you supply (email address and any message contents). The
        Service Provider may use this information to send important notices and, with separate
        consent, marketing communications.
      </p>

      <h3>Automatically Collected Information</h3>
      <p>
        The Application and the website may collect technical information automatically, including
        device type, mobile device unique identifier, IP address (anonymised in analytics),
        operating system, browser type, and aggregate information about how you interact with the
        Service.
      </p>

      {CITY.storeApps && (
        <>
          <h3>Tracking links and install attribution</h3>
          <p>
            Links of the form <code>{CITY.domain}/go/&lt;channel&gt;</code> tell us which channel
            (for example a post on Threads) brought a visitor. Each tap is logged with its time, the
            channel, the platform and the referring page — never the IP address — and then
            redirected to the App Store, Google Play or this website with the channel name attached.
          </p>
          <p>
            When the tap comes from an iPhone, the server also keeps a one-way keyed hash of the
            network address (never the address itself) in memory for up to 24 hours. When the
            Application is opened for the first time, it asks the server once whether a tap came
            from the same network within that window. If so, the Application records the channel and
            the time since the tap in analytics, and the hash is deleted at once. Nothing else is
            sent in that request, and it is made only by store builds that are allowed to send
            analytics. The key is discarded whenever the server restarts, so a hash cannot be linked
            to anything afterwards; hashes are never written to disk and never shared with third
            parties.
          </p>
          <p>
            On Android the same channel is read from the Google Play install referrer, which Google
            Play hands to the Application on its first launch; no network address is used.
          </p>
        </>
      )}
      <h2>Does the Service collect precise real-time location?</h2>
      <p>
        The mobile Application requests approximate device location (network or coarse GPS) only to
        anchor the map on your area and suggest nearby stations. Precise GPS is not collected. You
        can deny or revoke the location permission at any time in your device settings — the
        Application continues to function with manual station search.
      </p>

      <h2>Third parties</h2>
      <p>
        The third-party services listed below process data on our behalf. They receive usage events
        tied to pseudonymous identifiers (an installation or device identifier) — never your name,
        email address or precise location. Each provider has its own privacy policy governing what
        they do with the data they receive.
      </p>
      <ul>
        {CITY.webAnalytics ? (
          <WebAnalyticsProviders />
        ) : (
          <li>
            <a href="https://amplitude.com/privacy" target="_blank" rel="noopener noreferrer">
              Amplitude
            </a>{' '}
            — product analytics in the Application (event-level usage statistics). The website runs
            no analytics.
          </li>
        )}
        <li>
          <a href="https://www.inmobi.com/privacy-policy" target="_blank" rel="noopener noreferrer">
            InMobi Choice
          </a>{' '}
          — the cookie consent window on the website; stores your answer in the{' '}
          <code>euconsent-v2</code>, <code>addtl_consent</code>, <code>IABGPP_HDR_GppString</code>{' '}
          and <code>usprivacy</code> cookies and passes it to our analytics and advertising
          partners.
        </li>
        <li>
          <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer">
            Apple MapKit JS
          </a>{' '}
          — the street map on station pages of the website; receives map tile requests and the IP
          address they come from.
        </li>
        <li>
          <a
            href="https://www.appsflyer.com/legal/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
          >
            AppsFlyer
          </a>{' '}
          — install attribution for the Application. On iOS the advertising identifier (IDFA) is
          shared only if you allow tracking in the App Tracking Transparency prompt.
        </li>
        <li>
          <a
            href="https://cloud.google.com/maps-platform/terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google Maps Platform
          </a>{' '}
          — basemap rendering inside the Android Application; receives tile-request metadata.
        </li>
        <li>
          <a href="https://adapty.io/privacy" target="_blank" rel="noopener noreferrer">
            Adapty
          </a>{' '}
          — paywall and subscription state management for Application purchases; receives a
          pseudonymous user identifier and purchase events.
        </li>
      </ul>

      <p>The Service Provider may disclose information collected:</p>
      <ul>
        <li>as required by law (e.g. subpoena or similar legal process);</li>
        <li>
          where the Service Provider believes in good faith that disclosure is necessary to protect
          their rights, your safety or the safety of others, investigate fraud, or respond to a
          government request;
        </li>
        <li>
          to trusted service providers who work on the Service Provider's behalf, do not have an
          independent use of the information, and have agreed to adhere to the rules in this privacy
          statement.
        </li>
      </ul>

      <h2>Lawful basis</h2>
      <p>
        Consent (UK GDPR Art 6(1)(a)) — granted via the cookie consent window on the website and via
        OS-level permission prompts in the Application. Tracking-link attribution relies on our
        legitimate interest (Art 6(1)(f)) in knowing which channels bring users, limited to the
        hashed, 24-hour, in-memory processing described above.
      </p>

      <h2>Retention</h2>
      <p>
        {CITY.webAnalytics &&
          'Web cookies expire as defined by the respective providers (GA4 up to 14 months on the default setting). '}
        Your cookie consent record expires after 13 months. Mobile analytics events are aggregated
        and retained for up to 24 months, after which the Service Provider stores only aggregate
        data. User Provided Data (e.g. support contact) is retained as long as you use the Service
        and for a reasonable time thereafter.
      </p>

      <h2>Your opt-out rights</h2>
      <p>
        You can stop further collection by uninstalling the Application through your device's
        standard uninstall flow or your platform's app marketplace.
        {CITY.webAnalytics && (
          <>
            {' '}
            On the website, withdraw consent at any time via the{' '}
            <Link to="/cookie-policy">Cookie Policy</Link> page or the "Cookie settings" link in the
            footer.
          </>
        )}
      </p>

      <h2>Children</h2>
      <p>
        The Service Provider does not knowingly market to or solicit data from children under 13. If
        you are a parent or guardian and believe your child has provided personal information to the
        Service, contact <a href="mailto:support@mmthstudio.com">support@mmthstudio.com</a> and we
        will delete the data from our systems.
      </p>

      <h2>Security</h2>
      <p>
        The Service Provider applies physical, electronic, and procedural safeguards to protect the
        information processed. Access is restricted to authorised personnel who need it to operate
        or improve the Service. No security system can prevent every breach, but the Service
        Provider takes reasonable steps to minimise risk.
      </p>

      <h2>Your rights</h2>
      <p>
        You have rights of access, rectification, erasure, restriction, portability, and to lodge a
        complaint with the Information Commissioner's Office (ICO).
      </p>

      <h2>Changes</h2>
      <p>
        This Privacy Policy may be updated from time to time. The Service Provider will notify you
        of material changes by updating this page. Continued use after the change date constitutes
        acceptance of the revised policy.
      </p>

      <h2>Contact</h2>
      <p>
        Data subject requests: <a href="mailto:privacy@mmthstudio.com">privacy@mmthstudio.com</a>
        <br />
        General support: <a href="mailto:support@mmthstudio.com">support@mmthstudio.com</a>
      </p>
    </LegalLayout>
  )
}
